<template>
  <div>
    <!-- Generic Alert Dialog -->
    <md-dialog-alert
      :md-active.sync="error_dialog.show"
      :md-title="error_dialog.title"
      :md-content="error_dialog.content"
    />

    <!-- Snackbar -->
    <md-snackbar :md-active.sync="snackbar.show" :md-duration="8000">
      <span>
        <template v-if="snackbar.icon">
          <md-icon class="md-primary">{{ snackbar.icon }}</md-icon>
          &nbsp;&nbsp;&nbsp;
        </template>
        {{ snackbar.content }}
      </span>
      <md-button class="md-primary md-simple md-dense" @click="snackbar.show = false"
        >Dismiss</md-button
      >
    </md-snackbar>

    <group-card title="Submit to Lender" icon="contact_page" v-if="showSubmitToLender">
      <md-subheader>Lender</md-subheader>

      <div class="md-layout input-layout">
        <!-- Lender -->
        <validated-autocomplete-input
          class="md-layout-item md-small-size-100 md-size-50"
          label="Lender"
          v-model="application.loan.submission.current.product.lender_name"
          :options="available_lenders"
          :disabled="available_lenders == null"
          required
          force
        />

        <!-- Lenders Product -->
        <validated-autocomplete-input
          class="md-layout-item md-small-size-100 md-size-50"
          label="Tier"
          v-model="application.loan.submission.current.product.product_name"
          :options="available_products"
          :disabled="available_lenders == null"
          required
          force
        />
      </div>

      <template v-if="application.loan.asset.type == 'Personal'">
        <md-subheader>Asset Value</md-subheader>

        <div class="md-layout input-layout">
          <!-- Requested Amount -->
          <validated-number-input
            class="md-layout-item md-small-size-100 md-size-20"
            label="Requested Amount"
            prefix="$"
            v-model="security.requested_amount"
          >
          </validated-number-input>
        </div>
      </template>

      <template v-if="isWithOtherFields">
        <md-subheader>Asset Costs</md-subheader>

        <div class="md-layout input-layout">
          <!-- Purchase Cost -->
          <validated-input
            class="md-layout-item md-small-size-100 md-size-15"
            label="Purchase Cost"
            prefix="$"
            v-model="security.purchase_cost"
            type="CURRENCY"
          />

          <!-- Accessories -->
          <validated-input
            class="md-layout-item md-small-size-100 md-size-15"
            label="Extra Costs"
            prefix="$"
            v-model="security.accessories_cost"
            type="CURRENCY"
          />

          <!-- Deposit -->
          <validated-input
            class="md-layout-item md-small-size-100 md-size-15"
            label="Deposit"
            prefix="$"
            v-model="security.deposit_value"
            type="CURRENCY"
          />

          <!-- Trade In (calculated from the sum of trade in values in assets) -->
          <validated-input
            class="md-layout-item md-small-size-100 md-size-15"
            label="Trade In"
            prefix="$"
            v-model="trade_in_asset_value"
            type="CURRENCY"
            disabled
          />

          <!-- Finance Outstanding -->
          <validated-input
            class="md-layout-item md-small-size-100 md-size-15"
            label="Finance Outstanding"
            prefix="$"
            v-model="trade_in_asset_outstanding_amount"
            type="CURRENCY"
            :disabled="!enable_trade_in_asset_outstanding_amount"
          />

          <!-- Total Asset Cost -->
          <validated-input
            class="md-layout-item md-small-size-100 md-size-15"
            label="Total Asset Cost (calculated)"
            prefix="$"
            :value="total_requested_amount"
            type="CURRENCY"
            disabled
          />
        </div>
      </template>

      <md-subheader>Fees</md-subheader>

      <div class="md-layout input-layout">
        <!-- Document Origination Fee -->
        <validated-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="DOF"
          prefix="$"
          v-model="application.loan.submission.current.document_origination_fee"
          type="CURRENCY"
        />

        <!-- Establishment Fee -->
        <validated-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Establishment Fee"
          prefix="$"
          v-model="application.loan.submission.current.establishment_fee"
          type="CURRENCY"
        />

        <!-- PPSR Fee -->
        <validated-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="PPSR/Sundry Fee"
          prefix="$"
          v-model="application.loan.submission.current.ppsr_fee"
          type="CURRENCY"
        />
        <!-- Risk Fee -->
        <validated-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Risk Fee"
          prefix="$"
          v-model="application.loan.submission.current.risk_fee"
          type="CURRENCY"
        />

        <!-- Total Fees -->
        <validated-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Total Fees (calculated)"
          prefix="$"
          :value="total_fees"
          type="CURRENCY"
          disabled
        />

        <!-- Lender Commission -->
        <validated-input type="CURRENCY"
          class="md-layout-item md-small-size-100 md-size-15"
          label="Commission Inc in NAF (calculated)"
          prefix="$"
          readonly
          v-model="get_lender_commission_amount"
          disabled
        />

        <!-- Insurances -->
        <validated-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Total Insurance Cost (calculated)"
          prefix="$"
          :value="total_insurance_fees"
          type="CURRENCY"
          disabled
        />
      </div>

      <md-subheader>Repayments</md-subheader>

      <div class="md-layout input-layout">
        <!-- NAF -->
        <validated-number-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="NAF (calculated)"
          prefix="$"
          :value="naf"
          readonly
        >
        </validated-number-input>

        <!-- Rate -->
        <validated-number-input
          class="md-layout-item md-small-size-100 md-size-10"
          label="Rate"
          suffix="%"
          v-model="application.loan.submission.current.rate"
        >
        </validated-number-input>

        <!-- Balloon Payment -->
        <validated-number-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Balloon Amount"
          suffix="$"
          v-model="application.loan.submission.current.balloon_amount"
        >
        </validated-number-input>

        <!-- Term -->
        <validated-number-input
          class="md-layout-item md-small-size-100 md-size-10"
          label="Term"
          v-model="application.loan.submission.current.term"
          suffix="Months"
        >
        </validated-number-input>

        <!-- Frequency -->
        <validated-autocomplete-input
          class="md-layout-item md-small-size-100 md-size-10"
          label="Frequency"
          v-model="application.loan.submission.current.payment_frequency"
          :options="options.payment_frequency_options"
          force
        />

        <!-- Repayment Type-->
        <validated-autocomplete-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Repayment Type"
          v-model="application.loan.submission.current.repayment_type"
          :options="options.submission_repayment_type"
          force
        />

        <!-- Monthly account keeping fee -->
        <validated-number-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Monthly Account Keeping Fee"
          v-model="application.loan.submission.current.monthly_account_fee"
          prefix="$"
        />
        <!-- <div class="md-layout-item md-small-size-100 md-size-33" >
            <span v-if="term_in_months != null">
              ({{ term_in_months | yearsMonths }})
            </span>
          </div> -->
        <div></div>

        <!-- Repayments -->
        <validated-number-input
          class="md-layout-item md-small-size-100 md-size-15"
          label="Repayments"
          v-model="application.loan.submission.current.repayments"
          prefix="$"
          v-if="application.loan.submission.current.product.lender_name === 'Finance One (Commercial)' && application.loan.asset.type === 'Cashflow'"
        />
        <validated-text-input
          class="md-layout-item md-small-size-100 md-size-50"
          label="Repayments (calculated)"
          :value="repayment_message"
          readonly
          v-else
        >
        </validated-text-input>
        <!-- Uncommitted Income After this Repayment -->
        <validated-text-input
          class="md-layout-item md-small-size-100 md-size-50"
          label="Uncommitted Income After this Repayment"
          :value="uncommitted_income_after_repayment"
          readonly
        >
        </validated-text-input>
      </div>

      <md-subheader> Comment for the lender </md-subheader>

      <div class="md-layout input-layout">
        <!-- Comment -->
        <validated-text-input
          class="md-layout-item md-small-size-100 md-size-100"
          label="Comment for the lender"
          v-model="application.loan.submission.current.comment"
        />

        <div
          v-if="submissionValidation.warnings.length > 0 || zinkCapacity.disabled"
          class="md-layout-item md-small-size-100 md-size-100"
        >
          <b>Please address all listed issues in the comment above</b>
          <p
            v-for="warning of submissionValidation.warnings"
            :key="warning"
            class="md-layout-item md-small-size-100 md-size-100"
          >
            <note-view status="bad">{{ warning }}</note-view>
          </p>
          <p
            class="md-layout-item md-small-size-100 md-size-100"
            v-if="zinkCapacity.disabled"
          >
            <note-view status="bad">{{ zinkCapacity.warnings }}</note-view>
          </p>
        </div>
      </div>

      <div class="md-layout submit_buttons">
        <span>
          <md-tooltip
            md-direction="top"
            v-show="buttonDisabled('api')"
          >
            <span v-if="!product_properties.has_submission_api || !disableEmailAndUrl()">Unavailable for this Lender</span>
            <span v-else>Invalid Information</span>
          </md-tooltip>
          <md-tooltip
            md-direction="top"
            v-show="hasSubmitted"
          >
            <span>Notice: Previous API submission failed</span>
          </md-tooltip>
          <md-button
            class="md-primary submit-btn"
            @click="validateAndSubmit('api')"
            :disabled="buttonDisabled('api')"
          >
            Submit via API
          </md-button>
        </span>
        <span>
          <md-tooltip
            md-direction="top"
            v-show="buttonDisabled('email')"
          >
            <span v-if="submissionValidation.disableSubmit">Invalid Information</span>
            <span v-else-if="!product_properties.has_submission_email">Unavailable for this Lender</span>
            <span v-else-if="(product_properties.has_submission_api && !hasSubmitted) && disableEmailAndUrl() ">Available after API submission</span>
          </md-tooltip>
          <md-button
            class="md-primary submit-btn"
            @click="validateAndSubmit('email')"
            :disabled="buttonDisabled('email')"
          >
            Submit via Email
          </md-button>
        </span>
        <span>
          <md-tooltip
            md-direction="top"
            v-show="buttonDisabled('url')"
          >
            <span v-if="submissionValidation.disableSubmit">Invalid Information</span>
            <span v-else-if="!(product_properties.lender_name && product_properties.product_name)">Unavailable for this Lender</span>
            <span v-else-if="(product_properties.has_submission_api && !hasSubmitted) && disableEmailAndUrl() ">Available after API submission</span>
          </md-tooltip>
          <md-button
            class="md-primary submit-btn"
            @click="validateAndSubmit('url')"
            :disabled="buttonDisabled('url')"
          >
            Submit Manually
          </md-button>
        </span>
        <span>
          <AppReadinessCheck
            v-if='userHasRole("super-admin")'
            @onComplete="readinessCheck"
            :application="application"
            :disabled="buttonDisabled('api')">
        </AppReadinessCheck>
        </span>
      </div>
    </group-card>
    <upload-document-details
        :visible="upload_documents_details_dialog.show"
        :submission="upload_documents_details_dialog.submission"
        :documents="upload_documents_details_dialog.documents"
        :f1SupportedExt="upload_documents_details_dialog.f1SupportedExt"
        :value="application"
        :emailFlag="emailFlag"
        :submission_type="upload_documents_details_dialog.lenderName"
        @onCancel="upload_documents_details_dialog.show = false"
        @successUpload="uploadDocSuccess"
    >
    </upload-document-details>

    <!-- wisr Popup -->
    <div>
      <md-dialog :md-active.sync="wisrPopup">
        <md-dialog-title>Select Quote</md-dialog-title>
        <md-dialog-content class="md-layout">
          <v-data-table class="text-right"
            :headers="headers"
            :items="wisrQuotesResponse"
            filterable
            editable-columns
            title="popup"
          >
            <template v-slot:item.actions="{ item }">
              <loading-icon-button
                @click="selectWisrQuote(item)"
                tooltip="Select This Quote"
              >
                api
              </loading-icon-button>
            </template>
          </v-data-table>
        </md-dialog-content>
        <md-dialog-actions>
          <md-button class="md-primary" @click="closeWisrPopup">Close</md-button>
        </md-dialog-actions>

      </md-dialog>
    </div>

    <!-- nccp Popup -->
    <applicant-NCCP-modal
      :visible="showNccpPopup"
      @nccp-object-created="applicantNCCPObject"
      @closeNccpPopup="showNccpPopup = false"
    >
    </applicant-NCCP-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import filters from "@/components/utils/filters";
import utils from "@/components/utils/utils";
import { submissionValidation, isSurplusMoreThanZero } from "@/components/utils/validation";
import ValidatedInputs from "@/components/ValidatedInput";
import GroupCard from "@/components/Cards/GroupCard";
import { NoteView } from "@/components";
import WaitingDialog from "@/components/Dialogs/WaitingDialog";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import UploadDocumentDetails from "./UploadDocumentDetails";
import LoadingIconButton from "@/components/LoadingIconButton";
import { schema } from "../../validators/base"
import AppReadinessCheck from "./AppReadinessCheck"
import ApplicantNCCPModal from './ApplicantNccpModal.vue';

export default {
  components: {
    ...ValidatedInputs,
    GroupCard,
    NoteView,
    UploadDocumentDetails,
    LoadingIconButton,
    AppReadinessCheck,
    ApplicantNCCPModal,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    zinkCapacity: {
      type: Object,
      default: () => {
        return {
          disabled: false,
          warning: '',
        }
      }
    },
    suitabilityAssessment: {
        type: Boolean,
        default: false,
        required: true,
    }
  },
  data() {
    return {
      wisrPopup: false,
      wisrApplicationId:'',
      showNccpPopup: false,
      headers: [
        {
          text: 'ID',
          value: 'id',
          width: '150px',
        },
        {
          text: "Term",
          value: "term",
          sortable: false,
        },
        {
          text: "Is Eligible",
          value: "isEligible",
          sortable: false,
        },
        {
          text: "Rate",
          value: "rate",
        },
        {
          text: "Comparision Rate",
          value: "comparisionRate",
        },
        {
          text: "Monthly Repayment",
          value: "monthlyRepayment",
        },
        {
          text: "Total Repayment",
          value: "totalRepayment",
        },
        {
          text: "Eligible LoanAmount",
          value: "eligibleLoanAmount",
        },
        {
          text: "Estimate LoanAmount",
          value: "estimateLoanAmount",
        },
        {
          text: "Actions",
          value: "actions",
          align: "right",
          width: '130px',
          sortable: false,
          optional: false,
        },
      ],
      wisrQuotesResponse:[],
      error_dialog: {
        show: false,
        title: "",
        content: "",
      },
      snackbar: {
        show: false,
        icon: "done",
        content: "",
      },
      upload_documents_dialog: {
        show: false,
        submission: null,
        lender_name: null,
        product_name: null,
        status: null,
        application_status: null,
        wisrQuotesResponse_message: null,
        allows_settlement: null,
        documents: null,
        f1SupportedExt: null
      },
      upload_documents_details_dialog: {
        show: false,
        submission: false,
        documents: false,
        submitMethod:""
      },
      emailFlag: false,
      hasSubmitted: false,
      userIsSuperAdmin: false,
      greenLightAppId: ""
    };
  },
  watch: {
    "application.loan.submission.current.product.lender_name"(new_value, old_value) {
      if (old_value != null) {
        // do not set product to null when the product is belong to lender
        if (this.available_products == null ||
            !this.available_products.includes(this.application.loan.submission.current.product.product_name)
        ) {
          this.application.loan.submission.current.product.product_name = null;
        }
      }
    },
    "application.loan.submission.current.product.product_name"() {
      this.application.loan.submission.current.product.product_type = this.loan_type.product_type;
      this.application.loan.submission.current.product.asset_type = this.loan_type.asset_type;
      this.application.loan.submission.current.product.application_type =
        this.loan_type.application_type;
    },
    naf(value) {
      this.application.loan.submission.current.naf = value;
    },
    repayment_amount(value) {
      this.application.loan.submission.current.repayments = value;
    },
    trade_in_asset_value(value) {
      this.security.trade_in_value = value;
    }
  },
  computed: {
    // standard list of options from store
    ...mapGetters([
      "application",
      "application_status",
      "options",
      "properties",
      "productLenders",
      "products",
      "security",
      "uploadedDocuments",
      "user",
      "userHasRole",
      "agent_has_visibility_subscription",
      "requires_current_privacy_consent"
    ]),
    showSubmitToLender() {
        if (!this.agent_has_visibility_subscription && !this.application.loan.is_commercial && this.suitabilityAssessment) {
            return true;
        } else if (!this.agent_has_visibility_subscription && this.application.loan.is_commercial) {
            return true;
        }
        return false;
    },
    // internal reference to the v-model on this component, handles incomeing and outgoing changes
    application: {
      get() {
        return this.value;
      },
      set(application) {
        this.$emit("input", application);
      },
    },
    loan_type() {
      if (this.application) {
        if (this.application.loan.asset.type === "Personal Unsecured" || this.application.loan.asset.type === 'Personal Secured') {
          return {
            product_type: "loan",
            application_type: "consumer",
            asset_type: "personal",
          };
        } else {
          switch (this.application.loan.type) {
            case "Sole Trader":
            case "Partnership - ABN":
            case "Company":
            case "Trust":
              return {
                product_type: "loan",
                application_type: "commercial",
                asset_type: "vehicle",
              };
            case "Consumer":
              return {
                product_type: "loan",
                application_type: "consumer",
                asset_type: "vehicle",
              };
          }
        }
      } else {
        return null;
      }
    },
    available_lenders() {
      if (this.loan_type) {
        let { product_type, application_type, asset_type } = this.loan_type;
        return this.productLenders(product_type, application_type, asset_type);
      } else {
        return null;
      }
    },
    available_products() {
      if (this.available_lenders && this.application.loan.submission.current.product.lender_name) {
        let { product_type, application_type, asset_type } = this.loan_type;
        return this.products(
          product_type,
          application_type,
          asset_type,
          this.application.loan.submission.current.product.lender_name
        );
      } else {
        return null;
      }
    },
    product_properties() {
      let product_type = this.application.loan.submission.current.product.product_type;
      let application_type = this.application.loan.submission.current.product.application_type;
      let asset_type = this.application.loan.submission.current.product.asset_type;
      let lender_name = this.application.loan.submission.current.product.lender_name;
      let product_name = this.application.loan.submission.current.product.product_name;

      let properties =
        this.properties.products_properties &&
        this.properties.products_properties[product_type] &&
        this.properties.products_properties[product_type][application_type] &&
        this.properties.products_properties[product_type][application_type][asset_type] &&
        this.properties.products_properties[product_type][application_type][asset_type][lender_name] &&
        this.properties.products_properties[product_type][application_type][asset_type][lender_name][
          product_name
        ];

      return properties || {};
    },
    term_name() {
      switch (this.application.loan.submission.current.payment_frequency) {
        case "Weekly":
          return "Months";
        case "Fortnightly":
          return "Fortnights";
        case "Monthly":
          return "Months";
        case "Quarterly":
          return "Quarters";
        case "Yearly":
          return "Years";
        default:
          return "";
      }
    },
    term_in_months() {
      if (this.application.loan.submission.current.term == null) {
        return null;
      }

      let factor = filters.toMonthlyAmount(
        1,
        this.application.loan.submission.current.payment_frequency
      );

      return this.application.loan.submission.current.term / factor;
    },
    total_requested_amount() {
      let requested_amount = 0;
      switch (this.application.loan.asset.type) {
        case "Vehicle":
          requested_amount = this.float(this.application.loan.asset.vehicle.requested_amount);
          break;
        case "Personal Secured":
          requested_amount = this.float(this.application.loan.asset.secured.requested_amount);
          break;
        case "Personal Unsecured":
          requested_amount = this.float(this.application.loan.asset.unsecured.requested_amount);
          break;
        case "Marine":
          requested_amount = this.float(this.application.loan.asset.marine.requested_amount);
          break;
        case "Leisure":
          requested_amount = this.float(this.application.loan.asset.leisure.requested_amount);
          break;
        case "Cashflow":
          requested_amount = this.float(this.application.loan.asset.cashflow.requested_amount);
          break;
        case "Equipment":
          requested_amount = this.float(this.application.loan.asset.equipment.requested_amount);
          break;
      }
      let trade_in_asset_outstanding_amount = this.float(this.trade_in_asset_outstanding_amount);
      let trade_in_value = this.float(this.trade_in_asset_value);
      return requested_amount + trade_in_asset_outstanding_amount - trade_in_value;
    },
    total_fees() {
      let document_origination_fee = this.float(
        this.application.loan.submission.current.document_origination_fee
      );
      let establishment_fee = this.float(
        this.application.loan.submission.current.establishment_fee
      );
      let ppsr_fee = this.float(this.application.loan.submission.current.ppsr_fee);
      let risk_fee = this.float(this.application.loan.submission.current.risk_fee);

      return Math.round((document_origination_fee + establishment_fee + ppsr_fee + risk_fee) * 100) / 100;
    },
    total_insurance() {
      return this.application.loan.insurances.reduce(
        (sum, insurance) => sum + ((insurance.enabled && insurance.include_in_naf) ? this.float(insurance.premium) : 0),
        0
      );
    },
    total_insurance_fees() {
      return this.round(this.application.loan.insurances.reduce(
        (sum, insurance) => sum + this.float(insurance.premium),
        0
      ), 2);
    },
    naf: {
      get() {
        if (!this.application) {
          return null;
        }
        return Math.round((this.total_requested_amount + this.total_fees + this.total_insurance + this.get_lender_commission_amount) * 100) / 100;
      },
      set(value) {
        this.application.loan.submission.current.naf = value;
      }
    },
    trade_in_asset() {
      let assets = this.application.applicants.flatMap((a) => a.assets);
      return assets.find((a) => a.has_trade_in); // this returns undefined if no asset is selected as trade in
    },
    enable_trade_in_asset_value() {
      return !!this.trade_in_asset;
    },
    enable_trade_in_asset_outstanding_amount() {
      return this.enable_trade_in_asset_value && this.trade_in_asset.has_liability;
    },
    trade_in_asset_value: {
      get() {
        return this.enable_trade_in_asset_value ? (this.trade_in_asset.value === null ? this.security.trade_in_value : this.trade_in_asset.value) : null;
      },
      set(value) {
        if (this.enable_trade_in_asset_value) {
          this.trade_in_asset.value = value;
        }
      },
    },
    trade_in_asset_outstanding_amount: {
      get() {
        return this.enable_trade_in_asset_outstanding_amount
          ? this.trade_in_asset.finance.outstanding_amount
          : null;
      },
      set(value) {
        if (this.enable_trade_in_asset_outstanding_amount) {
          this.trade_in_asset.finance.outstanding_amount = value;
        }
      },
    },
    repayment_message() {
      let periodic_payments = this.currency(this.repayment_amount);
      let frequency = this.application.loan.submission.current.payment_frequency;
      let term = this.application.loan.submission.current.term;
      let number_of_payments = Math.round(
        term / utils.frequencyConversionFactor("Monthly", frequency)
      );

      if (periodic_payments) {
        return `${number_of_payments} ${frequency} payments of ${periodic_payments}`;
      } else {
        return null;
      }
    },
    submissionValidation() {
      let validation = submissionValidation(this.application);
      // disable submit when capacity less than 1 in consumer application
      if (this.loan_type.application_type === 'consumer' && validation.capacity.uncommitted_income < 1) {
        validation.notes.push('Uncommitted Income < $1');
        validation.disable_submit = true;
      }
      if(this.loan_type.application_type === 'consumer' && this.uncommitted_income_after_repayment_value < 1) {
        validation.notes.push('Uncommitted Income After this Repayment is less then $1');
        validation.disable_submit = true;
      } else if (this.uncommitted_income_after_repayment_value < 0) {
        validation.notes.push('Uncommitted Income After this Repayment is negative');
        validation.disable_submit = true;
      }
      return {
        warnings: validation.notes,
        disableSubmit: validation.disable_submit,
      };
    },
    isConsumerWithZeroSurplus() {
      let isConsumer = this.loan_type.application_type === 'consumer';
      return (!isSurplusMoreThanZero(this.application) && isConsumer);
    },
    repayment_amount: {
      get() {
        if (!this.application) {
          return null;
        }
        if(this.application.loan.submission.current.product.lender_name === 'Finance One (Commercial)' && this.application.loan.asset.type === 'Cashflow') {
          return null;
        }
        let amount = this.application.loan.submission.current.naf;
        let anual_rate = this.application.loan.submission.current.rate;
        let monthly_account_fee = this.application.loan.submission.current.monthly_account_fee;
        let term = this.application.loan.submission.current.term;
        let repayment_frequency = this.application.loan.submission.current.payment_frequency;
        let balloon_amount = this.application.loan.submission.current.balloon_amount;
        let repayment_type = this.application.loan.submission.current.repayment_type;

        return utils.calculateRepayments(
          amount,
          anual_rate,
          term,
          "Monthly",
          repayment_frequency,
          monthly_account_fee,
          balloon_amount,
          repayment_type,
        );
      },
      set(value) {
        this.application.loan.submission.current.repayments = value;
      }
    },
    isWithOtherFields(){
      let asset_types = ['Vehicle', 'Personal Secured', 'Marine', 'Leisure'];
      return asset_types.includes(this.application.loan.asset.type);
    },
    uncommitted_income_after_repayment_value() {
      if (this.loan_type.application_type === 'consumer')
      {
        return this.application.loan.capacity_and_prelim.uncommited_income - this.monthly_value
      }
      let validation = submissionValidation(this.application);
      return validation.capacity.uncommitted_income - this.application.loan.submission.current.repayments
    },
    uncommitted_income_after_repayment() {
      return this.currency(this.uncommitted_income_after_repayment_value)
    },
    get_lender_commission_amount: {
      get() {
        return this.application.loan.broker_income.lender_commission && this.application.loan.broker_income.lender_commission.include_in_naf
               ? this.float(this.application.loan.broker_income.lender_commission.amount) : 0
      },
      set(value) {
          this.application.loan.broker_income.lender_commission.amount = value;
      },
    },
    monthly_value () {
      const factor = utils.monthlyFrequencyConversionFactor(this.application.loan.submission.current.payment_frequency);
      return this.repayment_amount * factor
    }
  },
  mounted() {
    if (this.user.primary_roles.includes('super-admin')) {
      this.userIsSuperAdmin = true;
    }
  },
  methods: {
    // make all filters available as methods
    ...filters,
    applicantNCCPObject(applicantNCCP) {
      this.showNccpPopup = false;
      const selected_lender = this.application.loan.submission.current.product.lender_name;
      this.submit("api", selected_lender, {
        processName: "create-and-submit-application",
        NCCP: applicantNCCP,
      });
    },
    buttonDisabled(method) {
      // disable all when validation failed
      if (this.submissionValidation.disableSubmit || this.zinkCapacity.disabled) {
        return true
      }
      switch (method) {
        case 'api':
          return this.isApiButtonDisabled();
        case 'email':
          return this.isEmailButtonDisabled();
        case 'url':
          return this.isUrlButtonDisabled();
        default:
          return true;
      }
    },
    isApiButtonDisabled () {
      // if lender does not have api link disable
      if (!this.product_properties.has_submission_api) {
        return true;
      }
      // disable when api is not enable for this pod or lender, and not superadmin
      const podName = this.user.primary_pod_description;
      const lenderName = this.product_properties.lender_name;
      if (!this.isApiEnableForLenderAndPod(podName, lenderName) && !this.userIsSuperAdmin) {
        return true;
      }
      return false;
    },
    isEmailButtonDisabled () {
      // when lender dose not have email address, disable
      if (!this.product_properties.has_submission_email) {
        return true;
      }
      // disable when exist api and not submit
      if (this.product_properties.has_submission_api && !this.hasSubmitted) {
        return this.disableEmailAndUrl()
      }
      return false;
    },
    isUrlButtonDisabled () {
      // disable when there is no lender and product selected
      if (!(this.product_properties.lender_name && this.product_properties.product_name)) {
        return true;
      }
      // disable when exist api and not submit
      if (this.product_properties.has_submission_api && !this.hasSubmitted) {
        // enable when api is not avaliable for this pod or is superadmin
        return this.disableEmailAndUrl()
      }
      return false;
    },
    disableEmailAndUrl () {
      // enable when api is not avaliable for this pod or is superadmin
      const podName = this.user.primary_pod_description;
      const lenderName = this.product_properties.lender_name;
      if (!this.isApiEnableForLenderAndPod(podName, lenderName) || this.userIsSuperAdmin) {
        return false;
      }
      // disable when exist api and not submit
      return true;
    },
    async uploadDocSuccess(){
      let selected_lender = this.application.loan.submission.current.product.lender_name;
      this.upload_documents_details_dialog.show = false;
      let finance_one_names = this.options.lender_finance_one_name.filter((finance_one_name) => {
        return finance_one_name === selected_lender;
      })
      if (finance_one_names.length > 0) {
        this.submit(this.upload_documents_details_dialog.submitMethod, "Finance One");
      } else if (selected_lender == "Greenlight Auto") {
        this.submitGreenLight(this.greenLightAppId)
      } else if (
        this.upload_documents_details_dialog.lenderName === "Pepper Asset Finance (Consumer)" ||
        this.upload_documents_details_dialog.lenderName === "Pepper Asset Finance (Commercial)"
      ) {
        const payload = {
          submission_id: this.application.loan.submission.active.id,
          application_id: this.application.loan.id,
          processName: "pricing-plan-check",
        };
        const responseData = await this.submitPepperProcessToLender(payload);
        if (responseData) {
          const interestRate = responseData.interestRate;
          this.application.loan.submission.previous[0].rate = interestRate.toString();
          await this.saveApplication();
          await this.showPricingPlan(responseData);
        }
      }
    },

    async submitPepperProcessToLender(payload) {
      const processName = payload.processName.replaceAll("-"," ");
      try {
        this.$modal.show(WaitingDialog, {
          title: `Submitting application for ${processName}...`,
          message:
            "Please wait whilst we are working on process.",
        });
        const status = await this.$store.dispatch(
          "pepper_v3_check_status_submit_to_lender",
          {
            submission_id: payload.submission_id,
            application_id: payload.application_id,
            processName: payload.processName,
          }
        );
        if (status && status.submitted === true) {
          const data_api = JSON.parse(status.data || "{}");
          return data_api;
        } else {
          if (this.$modal.submit) {
            this.$modal.submit();
          }
          const errorMessage = this.handleSubmitError(
            this.upload_documents_details_dialog.submitMethod,
            status
          );
          this.showDialog(
            `Failed to submit ${processName} request to pepper.`,
            `The request cannot be submitted for some reasons - \n` +
              errorMessage
          );
        }
      } catch (error) {
        if (this.$modal.submit) {
          this.$modal.submit();
        }
        this.showDialog(
          `Failed to submit ${processName} request to pepper.`,
          `The request cannot be submitted for some reasons - \n` + error
        );
      }
    },

    async saveApplication() {
      try {
        this.$modal.show(WaitingDialog, {
          title: "Saving Application...",
          message: "Please wait whilst we are saving the application.",
        });

        // save the entire application
        await this.$store.dispatch("save_application", this.application);
      } catch (error) {
        if (this.$modal.submit) {
          this.$modal.submit();
        }
        this.showDialog(
          "Failed to save application.",
          `The application cannot be saved for some reasons - \n` +
            error?.response?.data
        );
        throw new Error("Failed to save application - " + error?.response?.data);
      }
    },
    async openEditSubmissionUploadDocumentsDialog(method, lenderName) {

      this.$modal.show(WaitingDialog, {
          title: "Saving Application...",
          message: "Please wait whilst we are saving the application.",
      });

      // save the entire application
      let result = await this.$store.dispatch("save_application", this.application);
      if (!result) {
          throw { error: "Failed to save application" };
      }

      if (this.$modal.submit) {
        this.$modal.submit();
      }

      const lendersRequirePreviousSubmission = ['Money3','Pepper Asset Finance (Consumer)','Pepper Asset Finance (Commercial)'];
      const currentSubmission = this.application.loan.submission.current;
      const previousSubmission = this.application.loan.submission.previous[0];
      this.upload_documents_dialog.show = false;
      this.upload_documents_details_dialog.show = false;
      this.upload_documents_details_dialog.submission = lendersRequirePreviousSubmission.includes(lenderName) ? previousSubmission : currentSubmission;
      this.upload_documents_details_dialog.documents = this.uploadedDocuments;
      this.upload_documents_details_dialog.show = true;
      this.upload_documents_details_dialog.submitMethod = method;
      this.upload_documents_details_dialog.lenderName = lenderName;
    },
    async readinessCheck(errors) {
      const validation = schema.application.validate(this.application, { abortEarly: false, allowUnknown: true });
      if (errors.hasError) {
        this.showDialog("Failed to submit application", `The application cannot be submitted for some reasons - \n` + errors.message);
      }
    },

    checkApplicationSaveRequired(selected_lender, custom_params) {
      switch (selected_lender) {
        case "Wisr (Auto)":
        case 'Wisr (Personal)':
          return custom_params?.wisrId === undefined;
        // TODO Pepperv3 BAUU-564 Disable until approval from stakeholder (also need to change integrator 2)
        // case "Pepper Asset Finance (Consumer)":
        // case "Pepper Asset Finance (Commercial)":
        //   return custom_params?.processName === "create-and-submit-application";
        default:
          return true;
      }
    },

    async submit(method = null, selected_lender = null, custom_params = {}) {
      let status = false;
      const isApplicationSaveRequired = this.checkApplicationSaveRequired(selected_lender, custom_params);
      if (isApplicationSaveRequired) {
        await this.saveApplication();
      }

      try {
        this.$modal.show(WaitingDialog, {
          title: "Submitting Application...",
          message: "Please wait whilst we are submitting the application.",
        });

        let application = {...this.application};

        if (selected_lender === "Finance One") {
            let uploadedDocument = this.$store.state.fin1_uploaded_document;
            custom_params.documentId = uploadedDocument
        }

        application.custom_params = custom_params;

        // request application submission
        switch (method) {
          case "api":
            status = await this.$store.dispatch("submit_application_via_api", application);
            break;
          case "email":
            status = await this.$store.dispatch("submit_application_via_email", application);
            break;
          case "url":
            status = await this.$store.dispatch("submit_application_via_url", application);
            break;
          default:
            status = await this.$store.dispatch("submit_application", application);
            break;
        }
        this.$modal.show(WaitingDialog, {
          title: "Submitting Application...",
          message: "Please wait whilst we are reloading the application.",
        });

        // re-load application from server
        await this.$store.dispatch("reload_application");
      } catch (error) {
        console.error(error);

        if (this.$modal.submit) {
          this.$modal.submit();
        }

        if (method === "api") {
          this.hasSubmitted = true;
        }

        this.showDialog("Failed to submit application.", `The application cannot be submitted for some reasons - \n` + error.response.data);
        throw "Failed to submit application - " + error.response.data;
      }

      // if the waiting dialog is open, close it
      if (this.$modal.submit) {
        this.$modal.submit();
      }

      // show approriate user feedback
      // status format status{submitted: ... , data: '{...}', action: ..., function: ...}
      if (status && status.submitted === true) {
        switch (status.action) {
          case "follow_url":
            this.error_dialog.title = "Manual Submission Required";
            let contentMessage = 'The application needs to be submitted manually';
            let importantMessage = '';
            let link = null;
            if (status.data) {
              importantMessage = contentMessage;
              contentMessage = 'Please click the button below to open Lender Portal.';
              link = status.data;
            }
            this.showConfirmationBox("Manual Submission Required", contentMessage, importantMessage, link);
            break;
          default:
            // NOTE the purpose of below condition code is the need to show upload documents modal right after application submission and not before that. As of now, only Money3 lender supports that.
            const data_api =  JSON.parse(status.data || "{}");
            console.log(data_api);
            if (selected_lender === "Money3") {
              this.openEditSubmissionUploadDocumentsDialog(method, selected_lender);
            }
            if ((selected_lender === "Wisr (Auto)" || selected_lender === "Wisr (Personal)") && custom_params.wisrId == undefined) {
              let data_api =  JSON.parse(status.data);
              this.wisrQuotesResponse = data_api.rateEstimate.indicativeQuotes;
              this.wisrApplicationId = data_api.wisrApplicationId;
              this.wisrPopup = true;
            } else if (selected_lender === "Greenlight Auto" && !custom_params.greenLightAutoAppId) {
                this.greenLightAppId = data_api.applicationId;
                this.openEditSubmissionUploadDocumentsDialog(method, selected_lender);
            // TODO Pepperv3 BAUU-564 Disable until approval from stakeholder (also need to change integrator 2)
            // } else if (
            //   selected_lender === "Pepper Asset Finance (Consumer)" ||
            //   selected_lender === "Pepper Asset Finance (Commercial)"
            // ) {
            //   if (custom_params.processName === 'create-and-submit-application') {
            //     if (data_api?.warning) {
            //       this.snackbar.content = `Application submitted successfully with warning.\n${data_api.warning}`;
            //       this.snackbar.icon = "warning";
            //       this.snackbar.show = true;
            //     }
            //     await this.openEditSubmissionUploadDocumentsDialog(
            //         method,
            //         selected_lender
            //     );
            //   }
            } else {
                this.snackbar.content = "Application submitted successfully";
                this.snackbar.icon = "done";
                this.snackbar.show = true;
            }
            break;
        }
      } else {
        const errorMessage = this.handleSubmitError(method,status)
        this.showDialog("Failed to submit application", `The application cannot be submitted for some reasons - \n` + errorMessage);
      }
    },

    async showPricingPlan(response)
    {
      //if theres an existing modal open close it
      if (this.$modal.submit) {
        this.$modal.submit();
      }
      const message = `<ul>
                <li><strong>Interest Rate:</strong> ${response.interestRate}</li>
                <li><strong>Commission:</strong> ${response.commission}</li>
                <li><strong>Net Amount Financed:</strong> ${response.netAmountFinanced}</li>
                <li><strong>Standard Repayment:</strong> ${response.standardRepayment}</li>
                <li><strong>Repayment:</strong> ${response.repayment}</li>
                <li><strong>Disclaimer:</strong> ${response.disclaimer}</li>
            </ul>
        `;
      return await this.showDialog("Pricing Plan", message)
    },

    handleSubmitError(method,status) {
        if (method === "api") {
          this.hasSubmitted = true;
        }

        let errorMessage = '';
        if (status.data) {
          let errorData = JSON.parse(status.data);
          if (Array.isArray(errorData)) {
            Object.values(errorData).forEach((element) => {
              let path_hint = element.field;
              if (path_hint != undefined) {
                let fieldArr = path_hint.split(".");
                let field = filters.capitalize(fieldArr[fieldArr.length -1]);
                errorMessage += `<p>${field} ${(element.message.toLowerCase().trim())}</p>`;
              } else {
                errorMessage += `<p>${(element.message)}</p>`;
              }
            });
          } else {
            let path_hint = errorData.field;
            if (path_hint != undefined) {
                let fieldArr  = path_hint.split(".");
                let field     = filters.capitalize(fieldArr[fieldArr.length -1]);
                errorMessage += `<p>${field} ${(errorData.message.toLowerCase().trim())}</p>`;
              } else {
                errorMessage += `<p>${(errorData.message)}</p>`;
              }
          }
        }

        if (errorMessage === '') {
          errorMessage = JSON.stringify(status);
        }

        return errorMessage;
    },
    showDialog(title, content){
      this.error_dialog.title = title;
      this.error_dialog.content = content;
      this.error_dialog.show = true;
    },
    async showConfirmationBox(title, content, impMessage, link=null)
    {
      //if theres an existing modal open close it
      if (this.$modal.submit) {
        this.$modal.submit();
      }
      let wisrQuotesResponse = await this.$modal.show(ConfirmDialog,{
        title: title,
        message: content,
        important: impMessage,
        confirmText: link ? 'Open Lender Portal' : null,
        cancelText: 'Close',
      });
      if (wisrQuotesResponse) {
        window.open(link, '_blank').focus();
      }
    },

    async showDisclaimerDialog(disclaimer, type)
    {
      //if theres an existing modal open close it
      if (this.$modal.submit) {
        this.$modal.submit();
      }
      return await this.$modal.show(ConfirmDialog,{
        title: "Disclaimer",
        message: type == "application" ?  `<ul>
                <li><strong>Acknowledgment:</strong>
                    <ul>
                        <li><strong>Statement</strong>${disclaimer.acknowledgment.statement}</li>
                        <li><a href="${disclaimer.acknowledgment.privacyPolicyLink}" target="_blank"><strong>Privacy Policy Link</strong></a></li>
                        <li><a href="${disclaimer.acknowledgment.creditReportingPolicyLink}" target="_blank"><strong>Credit Reporting Policy Link</strong></a></li>
                        <li><a href="${disclaimer.acknowledgment.statementOfNotifiableMattersLink}" target="_blank"><strong>Statement Of Notifiable Matters Link</strong></a></li>
                    </ul>
                </li>
                <li><strong>Customer Consent:</strong> ${disclaimer.customerConsent}</li>
                <li><strong>Privacy Policy:</strong> ${disclaimer.privacyPolicy}</li>
            </ul>
        `:`<ul>
                <li><strong>Intro:</strong> ${disclaimer.intro}</li>
                <li><strong>Have Authority:</strong> ${disclaimer.haveAuthority}</li>
                <li><strong>Read Statement:</strong> ${disclaimer.readStatement}</li>
                <li><strong>Borrower Consents:</strong> ${disclaimer.borrowerConsents}</li>
                <li><strong>Borrower Authorisation:</strong> ${disclaimer.borrowerAuthorisation}</li>
                <li><strong>True Information:</strong> ${disclaimer.trueInformation}</li>
                <li><strong>Laws Compliance:</strong> ${disclaimer.lawsCompliance}</li>
                <li><strong>Asess Responsibility:</strong> ${disclaimer.assessResponsibility}</li>
            </ul>
        `,
        important: "",
        confirmText: `Proceed`,
        cancelText: 'Cancel',
      });
    },
    async validateAndSubmit(method = null) {
      if (this.isConsumerWithZeroSurplus) {
        this.showDialog('Validation Error', 'Please make sure this application has surplus amount > 0.');
      } else {
        // check all applicants privacy consent
        if (this.requires_current_privacy_consent) {
          if (this.$modal.submit) {
            this.$modal.submit();
          }
          this.showDialog('Validation Error', 'Application cannot be submitted without current privacy consent from all applicants.');
          return;
        };
        // check addresses tenure
        if (!await this.checkAddressesTenure()) {
          return;
        }

        let selected_lender = this.application.loan.submission.current.product.lender_name;
        // this.options.lender_finance_one_name from the lender db table
        let finance_one_names = this.options.lender_finance_one_name.filter((finance_one_name) => {
          return finance_one_name == selected_lender;
        })
        if (finance_one_names.length > 0) {
          if (!this.validateFin1()) {
            return false;
          }
        }
        const money3_lenders = ['Money3', "Money3 Personal Loans"];
        if (method === 'api') {
          if (finance_one_names.length > 0) {
            selected_lender = "Finance One";
            this.getF1ValidFileExtensions();
            this.openEditSubmissionUploadDocumentsDialog(method, selected_lender);
          } else if (selected_lender ===  "Greenlight Auto") {
            this.submitProcessToGreenLightAutoQuote(method, selected_lender);
          } else if (money3_lenders.includes(selected_lender)) {
            this.submit(method, "Money3");
          // TODO Pepperv3 BAUU-564 Disable until approval from stakeholder (also need to change integrator 2)
          // } else if (selected_lender === "Pepper Asset Finance (Consumer)" || selected_lender === "Pepper Asset Finance (Commercial)") {
          //   this.showNccpPopup = true;
          } else {
            this.submit(method, selected_lender);
          }
        } else if(method === 'email'){
            this.emailFlag = true;
            this.openEditSubmissionUploadDocumentsDialog(method, selected_lender);
        }
        else {
          this.submit(method);
        }
      }
    },
    async getF1ValidFileExtensions() {
      try {
        const response = await axios.get('/api/v1/application/finance_one/get_whitelisted_file_extensions');
        this.upload_documents_details_dialog.f1SupportedExt = response.data;
      } catch (e) {
        console.error(e);
      }
    },

    async submitProcessToGreenLightAutoQuote(method, selected_lender) {
        try {
            this.$modal.show(WaitingDialog, {
                title: "Submitting Application...",
                message: "Please wait while we are fetch the disclaimer.",
            });
            const disclaimerData = await this.getGreenLightDisclaimer("calculate");
            const accepted = await this.showDisclaimerDialog(disclaimerData.data.data.data, "calculate");
            if(accepted) {
                this.submit(method, selected_lender);
            }
        } catch (error) {
            if (this.$modal.submit) {
                this.$modal.submit();
            }
            this.showDialog("Failed to submit application.", `The application cannot be submit for some reasons - \n` + error.wisrQuotesResponse.data);
            throw "Failed to save application - " + error.wisrQuotesResponse.data;
        }
    },

    closeWisrPopup() {
      this.wisrPopup = false;
    },
    async selectWisrQuote(quote) {
      this.wisrPopup = false;
      await this.submit('api', 'Wisr (Auto)', {'wisrId': this.wisrApplicationId, 'quoteId': quote.id});
    },

    async submitGreenLight(appId) {
      try {

        this.$modal.show(WaitingDialog, {
            title: "Submitting Application...",
            message: "Please wait while we are fetch the desclaimer.",
        });
        const disclaimerData = await this.getGreenLightDisclaimer("application");
        const accepted = await this.showDisclaimerDialog(disclaimerData.data.data.data, "application");
        if(accepted) {
            await this.submit('api', 'Greenlight Auto', {'greenLightAutoAppId': appId});
        }
      } catch (error) {
        if (this.$modal.submit) {
          this.$modal.submit();
        }
        this.showDialog("Failed to submit application.", `The application cannot be submit for some reasons - \n` + error.data);
        throw "Failed to save application - " + error.data;
      }
      this.$modal.submit();
    },

    validateFin1() {
      let titleError = "";
      const minExpRequired = 36
      this.application.applicants.forEach(applicant => {
        applicant.employments.current.forEach(employment => {
          if (!employment.employer_details.name) {
            titleError = titleError +' '+ `${applicant.contact.name.firstname} ${applicant.contact.name.lastname}'s employment "${employment.title}" employer name is blank<br/>`;
          }
        })
        let totalExp = 0;
        let highestCurrentExperience = 0;
        applicant.employments.current.forEach(employment => {
          const exp = (parseInt(employment.duration.years || 0) * 12) + parseInt(employment.duration.months || 0);
          if (exp > highestCurrentExperience) {
            highestCurrentExperience = exp;
          }
        })
        totalExp = highestCurrentExperience;
        applicant.employments.previous.forEach(employment => {
          totalExp = totalExp + (parseInt(employment.duration.years || 0) * 12) + parseInt(employment.duration.months || 0);
        })

        if (totalExp < minExpRequired) {
          titleError = titleError +' '+ `Require 3 years of employment history for ${applicant.contact.name.firstname} ${applicant.contact.name.lastname}<br/>`;
        }
      })

      if (titleError) {
        this.showDialog('Validation Error', titleError);
        return false;
      }
      return true
    },
    // TODO hardcode for function, must be changed in the future
    // TODO rewrite function to support dynamic lenders and pods
    // when lender is in specific lenders and user not in specific pod, disable
    isApiEnableForLenderAndPod (podName='', lenderName='') {
      // TODO hardcode for all available lenders, must be changed in future
      const availableLenders = [
        'Wisr (Auto)',
        'Wisr (Personal)',
        'Plenti (Personal Loans)',
        'Plenti Auto (Commercial)',
        'Plenti Auto (Consumer)',
        'Affordable Car Loans'
      ];
      // TODO hardcode for all available pods, must be changed in future
      const avaliablePods = [
        'Internal Broker Pod'
      ];
      if (availableLenders.includes(lenderName)) {
        return avaliablePods.includes(podName);
      }
      return true;
    },

    async getGreenLightDisclaimer(type) {
      let selected_lender = this.application.loan.submission.current.product.lender_name;
      return await axios.get(`/api/v1/application/${this.application.loan.id}/greenlight-desclaimer?type=${type}&lender=${selected_lender}`);
    },

    async checkAddressesTenure() {
      //if theres an existing modal open close it
      if (this.$modal.submit) {
        this.$modal.submit();
      }

      if (!this.isAnyOfPreviousAddressesOneMonth()) {
        // when all of previous greater than 1 months, pass the check
        return true;
      }

      return await this.$modal.show(ConfirmDialog, {
        title: "Do you wish to continue the process?",
        important: "Previous address is auto populated, please validate before lender submission.",
        confirmText: 'Continue submitting',
        cancelText: 'Close',
      });
    },

    isAnyOfPreviousAddressesOneMonth() {
      const addresses = this.application.applicants.map(a => a.addresses);
      const previous_durations = addresses[0].previous.map(address => (address.duration.years * 12) + Number(address.duration.months));
      return previous_durations.some(duration => duration <= 1);
    },
  },

  async beforeCreate() {
    const params = {
      types: [
        'lender_finance_one_name',
        'payment_frequency_options',
        'products_properties',
        'submission_repayment_type',
      ],
    }
    await this.$store.dispatch('fetch_types', { params });
  },

  created() {
    // for existing applications where the naf hasnt been saved, backfill it from the computed value
    if (this.naf > 0 && this.naf != this.application.loan.submission.current.naf) {
      this.application.loan.submission.current.naf = this.naf;
    }
    // for existing applications where the repayment amount hasnt been saved, backfill it from the computed value
    if (this.repayment_amount > 0 && this.repayment_amount != this.application.loan.submission.current.repayments) {
      this.application.loan.submission.current.repayments = this.repayment_amount;
    }
    // for existing applications where the trade in value in asset is not set
    if (this.trade_in_asset_value > 0 && this.trade_in_asset_value != this.security.trade_in_value) {
      this.security.trade_in_value = this.trade_in_asset_value;
    }
  },
  filters: {
    // make all filters available as filters
    ...filters,
  },
};
</script>
<style scoped lang="scss">
@import "@/assets/scss/theme.scss";
.submit_buttons {
  margin-top: 16px;

  & span {
    margin-right: 5px;
  }
}

.submit-btn {
  background-color: $ausloans-green-dark !important;
}

.submit-btn:hover {
  background-color: $ausloans-green !important;
}

.submit-btn:disabled {
  background-color: $ausloans-red !important;
}

.submit-btn-red {
  background-color: $ausloans-red !important;
}

.submit-btn-red:hover {
  background-color: $ausloans-red !important;
  opacity: 0.8;
}

</style>
